import { useEffect, useRef, useState } from "react";
import { Toast, ToastMessage } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import desiriRoleService from "../../services/desiriRoles/rolesservice"; 

interface Props {
  onSearch: (text: string) => void;
  onRefresh: () => void;
}

const DesiriRolesSearch: React.FC<Props> = ({ onSearch, onRefresh }) => {
  const toast = useRef<Toast>(null);
  const [searchText, setSearchText] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    designationName: "",
    designationDescription: "",
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleSearch = () => onSearch(searchText.trim());

  const resetFields = () => {
    setSearchText("");
    onSearch("");
  };

  const resetForm = () => {
    setFormData({
      designationName: "",
      designationDescription: "",
    });
    setErrors({});
  };

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

const handleSave = async () => {
  const newErrors: { [key: string]: string } = {};

  if (!formData.designationName?.trim()) {
    newErrors.designationName = "Designation Name is required";
  }
 

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  const payload = {
    name: formData.designationName,
    description: formData.designationDescription,
  };

  try {
    const result = await desiriRoleService.addRole(payload);

    if (result?.isSuccess) {
      showMessage("Success", result.message || "Role added successfully", "success");
      resetForm();
      setShowAddModal(false);
      onRefresh();
    } else {
      showMessage("Failed", result?.message || "Failed to add role", "error");
    }
  } catch (error) {
    console.error("Error saving role:", error);
    showMessage("Error", "Server error while saving role", "error");
  }
};
 const fieldHoverStyle = (fieldName: string) => ({
    border:
      hoveredField === fieldName ? "2px solid #6366F1" : "1px solid #ced4da",

    boxShadow:
      hoveredField === fieldName ? "0 0 6px rgba(99, 102, 241, 0.3)" : "none",
    transition: "all 0.2s ease-in-out",
    width: "100%",
    minHeight: "40px",
  });
  return (
    <div className="col-12">
      <Toast ref={toast} />

      {/* Search & Action */}
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div
          className="input-container font-bold text-white border-round"
          style={{ float: "left", width: "18%" }}
        >
          <div className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search Roles"
              style={{ width: "100%" }}
            />
          </div>
        </div>

       <div className="buttons-container">
          <div
            className="filter-button font-bold text-white border-round"
            style={{ float: "left", width: "10%" }}
          >
            <Button
              className="text-black border-round"
              style={{ width: "100%" }}
              onClick={handleSearch}
              severity="success"
            >
              Filter
            </Button>
          </div>
          <div
            className="filter-button font-bold text-white border-round ml-2"
            style={{ float: "left", width: "10%" }}
          >
            <Button
              className="text-black border-round"
              style={{ width: "100%" }}
              onClick={resetFields}
              severity="secondary"
              text
              raised
            >
              Reset
            </Button>
          </div>
          <div
            className="add-button-container border-round font-bold ml-3 text-primary"
            style={{ textAlign: "right" }}
          >
            <Button
              className="bg-blue-700 border-round p-button p-button-text p-component text-white"
              onClick={() => setShowAddModal(true)}
              severity="secondary"
              text
              raised
            >
              Add Role
            </Button>
          </div>
        </div>
      </div>

      {/* Add Role Modal */}
      <Dialog
        visible={showAddModal}
        onHide={() => {
          resetForm();
          setShowAddModal(false);
        }}
        className="quickview w-50"
        modal
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
      >

          <h5 className="border-round mb-3 order-top-color p-2 panel-header-text text-center">
        ADD DESIRI DESIGNATION
        </h5>
        

        <div className="grid">
          <div className="col-12 mb-2">
            <div className="grid">
              {/* Designation Name */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Designation Name<span className="required">*</span>
                </label>
                <InputText
                  value={formData.designationName}
                  onChange={(e) =>
                    handleInputChange("designationName", e.target.value)
                  }
                  placeholder="Enter Role Name"
                  style={fieldHoverStyle("designationName")}
                />
                {errors.designationName && (
                  <small className="p-error">{errors.designationName}</small>
                )}
              </div>

              {/* Designation Description */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">Description</label>
                <InputText
                  value={formData.designationDescription}
                  onChange={(e) =>
                    handleInputChange("designationDescription", e.target.value)
                  }
                  placeholder="Enter Description"
                  style={fieldHoverStyle("designationDescription")}
                />
              </div>

              {/* Save */}
              <div className="field col-12 text-right mt-5">
                <Button
                  className="bg-green-700 text-white"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DesiriRolesSearch;
